import { supabase } from './supabase';

export const joinDinner = async (dinnerId: string) => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!user) throw new Error('You must be logged in to join a dinner');

    // Check if user is already a participant
    const { data: existingParticipant, error: checkError } = await supabase
      .from('dinner_participants')
      .select('id')
      .eq('dinner_id', dinnerId)
      .eq('user_id', user.id)
      .maybeSingle();

    if (checkError) throw checkError;
    if (existingParticipant) throw new Error('You have already joined this dinner');

    // Get the dinner to check availability
    const { data: dinner, error: dinnerError } = await supabase
      .from('dinners')
      .select('spots_available, status')
      .eq('id', dinnerId)
      .single();

    if (dinnerError) throw dinnerError;
    if (dinner.spots_available <= 0) throw new Error('This dinner is full');
    if (dinner.status !== 'open') throw new Error('This dinner is not available for joining');

    // Add user as participant
    const { data: participant, error: participantError } = await supabase
      .from('dinner_participants')
      .insert({
        dinner_id: dinnerId,
        user_id: user.id
      })
      .select()
      .single();

    if (participantError) throw participantError;

    // Update dinner spots available
    const { error: updateError } = await supabase
      .from('dinners')
      .update({
        spots_available: dinner.spots_available - 1,
        status: dinner.spots_available - 1 <= 0 ? 'full' : 'open'
      })
      .eq('id', dinnerId);

    if (updateError) throw updateError;

    // Increment user's dinners_count
    const { error: profileUpdateError } = await supabase
      .from('profiles')
      .update({
        dinners_count: (user.dinners_count || 0) + 1
      })
      .eq('id', user.id);

    if (profileUpdateError) {
      console.error('Error updating dinners count:', profileUpdateError);
      // Don't throw here, as the dinner join was successful
    }

    return participant;
  } catch (err) {
    console.error('Error joining dinner:', err);
    throw err;
  }
};

export const joinBlindDate = async (blindDateId: string) => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!user) throw new Error('You must be logged in to join a blind date');

    // Check if user is already a participant
    const { data: existingParticipant, error: checkError } = await supabase
      .from('blind_date_participants')
      .select('id')
      .eq('blind_date_id', blindDateId)
      .eq('user_id', user.id)
      .maybeSingle();

    if (checkError) throw checkError;
    if (existingParticipant) throw new Error('You have already joined this blind date');

    // Get the blind date to check availability
    const { data: blindDate, error: blindDateError } = await supabase
      .from('blind_dates')
      .select('spots_available, status')
      .eq('id', blindDateId)
      .single();

    if (blindDateError) throw blindDateError;
    if (blindDate.spots_available <= 0) throw new Error('This blind date is full');
    if (blindDate.status !== 'open') throw new Error('This blind date is not available for joining');

    // Add user as participant
    const { data: participant, error: participantError } = await supabase
      .from('blind_date_participants')
      .insert({
        blind_date_id: blindDateId,
        user_id: user.id
      })
      .select()
      .single();

    if (participantError) throw participantError;

    // Update blind date spots available
    const { error: updateError } = await supabase
      .from('blind_dates')
      .update({
        spots_available: blindDate.spots_available - 1,
        status: blindDate.spots_available - 1 <= 0 ? 'full' : 'open'
      })
      .eq('id', blindDateId);

    if (updateError) throw updateError;

    // Increment user's dinners_count
    const { error: profileUpdateError } = await supabase
      .from('profiles')
      .update({
        dinners_count: (user.dinners_count || 0) + 1
      })
      .eq('id', user.id);

    if (profileUpdateError) {
      console.error('Error updating dinners count:', profileUpdateError);
      // Don't throw here, as the blind date join was successful
    }

    return participant;
  } catch (err) {
    console.error('Error joining blind date:', err);
    throw err;
  }
};

export const joinDrinkingNight = async (drinkingNightId: string) => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!user) throw new Error('You must be logged in to join a drinking night');

    // Check if user is already a participant
    const { data: existingParticipant, error: checkError } = await supabase
      .from('drinking_night_participants')
      .select('id')
      .eq('drinking_night_id', drinkingNightId)
      .eq('user_id', user.id)
      .maybeSingle();

    if (checkError) throw checkError;
    if (existingParticipant) throw new Error('You have already joined this drinking night');

    // Get the drinking night to check availability
    const { data: drinkingNight, error: drinkingNightError } = await supabase
      .from('drinking_nights')
      .select('spots_available, status')
      .eq('id', drinkingNightId)
      .single();

    if (drinkingNightError) throw drinkingNightError;
    if (drinkingNight.spots_available <= 0) throw new Error('This drinking night is full');
    if (drinkingNight.status !== 'open') throw new Error('This drinking night is not available for joining');

    // Add user as participant
    const { data: participant, error: participantError } = await supabase
      .from('drinking_night_participants')
      .insert({
        drinking_night_id: drinkingNightId,
        user_id: user.id
      })
      .select()
      .single();

    if (participantError) throw participantError;

    // Update drinking night spots available
    const { error: updateError } = await supabase
      .from('drinking_nights')
      .update({
        spots_available: drinkingNight.spots_available - 1,
        status: drinkingNight.spots_available - 1 <= 0 ? 'full' : 'open'
      })
      .eq('id', drinkingNightId);

    if (updateError) throw updateError;

    // Increment user's dinners_count
    const { error: profileUpdateError } = await supabase
      .from('profiles')
      .update({
        dinners_count: (user.dinners_count || 0) + 1
      })
      .eq('id', user.id);

    if (profileUpdateError) {
      console.error('Error updating dinners count:', profileUpdateError);
      // Don't throw here, as the drinking night join was successful
    }

    return participant;
  } catch (err) {
    console.error('Error joining drinking night:', err);
    throw err;
  }
};